import { useState, useEffect } from 'react';
import { YES_NO, YesNoDataSource } from 'components/v2/compiler/constants';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { LabelGroup } from 'components/v2/form/styled';
import { HelpBox } from 'components/v2/help-boxes';
import { StatusAttentionIcon } from 'components/v2/icons';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { useTranslation } from 'react-i18next';
import { EPDModel } from 'services/EpdClient';
import styled from 'styled-components';
import { FieldContainer, FieldPanelInput } from 'styles/v2/Styles.styled';
import { Option } from 'types/types';

type SectionProps = {
  epd: EPDModel;
  biogenicCarbonContentUnits?: Option[];
  onChangeEpd(name: string, value: any): Promise<void>;
  isConstruction?: boolean;
};

const BiogenicCarbonContentCopySection: React.FC<SectionProps> = ({
  epd,
  biogenicCarbonContentUnits,
  onChangeEpd,
  isConstruction,
}) => {
  const { t } = useTranslation();
  // I'm sorry for type-safety here, we need this for the release, and the proper fix is
  // to rewrite everything in copy group using optimistic mutations.
  const [optimisticValue, setOptimisticValue] = useState<any>(epd.biogenicCarbonContent ?? {});
  const isProductContentRequired = optimisticValue?.doesProductContainMoreThan5Percent === YES_NO.YES;
  const isPackageContentRequired = optimisticValue?.doesPackagingMaterialContainMoreThan5Percent === YES_NO.YES;

  useEffect(() => {
    const current = optimisticValue;
    const incoming = epd.biogenicCarbonContent;

    if (
      current?.doesProductContainMoreThan5Percent !== incoming?.doesProductContainMoreThan5Percent ||
      current?.doesPackagingMaterialContainMoreThan5Percent !== incoming?.doesPackagingMaterialContainMoreThan5Percent ||
      current?.productItem?.conversionFactor !== incoming?.productItem?.conversionFactor ||
      current?.productItem?.unitKey !== incoming?.productItem?.unitKey ||
      current?.accompanyingPackageItem?.conversionFactor !== incoming?.accompanyingPackageItem?.conversionFactor ||
      current?.accompanyingPackageItem?.unitKey !== incoming?.accompanyingPackageItem?.unitKey
    ) {
      setOptimisticValue(incoming);
    }
  }, [epd.biogenicCarbonContent]);

  function handleConversionFactorChange(event: any) {
    const { value } = event.target;
    const formattedProductFlowValue = value.replace(/^0+(?=\d)/, ''); // Removes leading zeros, except one before a period
    event.target.value = formattedProductFlowValue;
  }

  const handleConversionFactorKeyDown = (event: any) => {
    const {
      code,
      key,
      ctrlKey,
      metaKey,
      target,
    } = event;
    const value = (target as HTMLInputElement).value;
    const fractionalNumWithOneDigitPattern = /^0\.\d$/;
    if (code === 'Backspace' && fractionalNumWithOneDigitPattern.test(value)) {
      event.target.value = '';
      return;
    }
    if (code === 'Minus') {
      event.preventDefault();
      return;
    }
    // Block ArrowDown when value would become negative
    if (key === 'ArrowDown') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue <= 0) {
        event.preventDefault();
        return;
      }
    }
    if (ctrlKey || metaKey) return;
    // Hard cap: block digit input when already at 8 decimal places
    const isDigit = key.length === 1 && key >= '0' && key <= '9';
    if (!isDigit) return;
    const selectionStart = (target as HTMLInputElement).selectionStart ?? value.length;
    const selectionEnd = (target as HTMLInputElement).selectionEnd ?? value.length;
    const nextValue = value.slice(0, selectionStart) + key + value.slice(selectionEnd);
    const dotIndex = nextValue.indexOf('.');
    if (dotIndex !== -1 && nextValue.length - dotIndex - 1 > 8) {
      event.preventDefault();
    }
  };

  const handleConversionFactorPaste = (event: any) => {
    event.preventDefault();
    const clipboardData = event.clipboardData || window.Clipboard;
    const pastedValue = clipboardData.getData('text');

    let formatted = pastedValue
      .replace(/^0+(?=\d)/, '') // Removes leading zeros, except one before a period
      .replace(/-/g, ''); // Removes minuses

    // Truncate to 8 decimal places
    const dotIndex = formatted.indexOf('.');
    if (dotIndex !== -1 && formatted.length - dotIndex - 1 > 8) {
      formatted = formatted.slice(0, dotIndex + 9);
    }

    (event as React.ClipboardEvent<HTMLInputElement>).currentTarget.value = formatted;
  };

  const handleChangeProperty = (propertyName: string, value: any) => {
    const updatedBiogenicCarbonContent = changeBiogenicCarbonContentValues(propertyName, value);
    setOptimisticValue(updatedBiogenicCarbonContent);
    onChangeEpd('biogenicCarbonContent', updatedBiogenicCarbonContent);
  };

  const changeBiogenicCarbonContentValues = (name: string, value?: string) => {
    switch (name) {
      case 'doesProductContainMoreThan5Percent':
        return {
          ...optimisticValue,
          doesProductContainMoreThan5Percent: value,
        };
      case 'doesPackagingMaterialContainMoreThan5Percent':
        return {
          ...optimisticValue,
          doesPackagingMaterialContainMoreThan5Percent: value,
        };
      case 'productItemUnitKey':
        return {
          ...optimisticValue,
          productItem: { ...optimisticValue?.productItem, unitKey: value },
        };
      case 'productItemConversionFactor':
        return {
          ...optimisticValue,
          productItem: { ...optimisticValue?.productItem, conversionFactor: value },
        };
      case 'accompanyingPackageItemUnitKey':
        return {
          ...optimisticValue,
          accompanyingPackageItem: { ...optimisticValue?.accompanyingPackageItem, unitKey: value },
        };
      case 'accompanyingPackageItemConversionFactor':
        return {
          ...optimisticValue,
          accompanyingPackageItem: { ...optimisticValue?.accompanyingPackageItem, conversionFactor: value },
        };
      default:
        return { ...optimisticValue, [name]: value };
    }
  };

  const isSectionValid = () => {
    if (!isConstruction) {
      return true;
    }

    let biogenicProductConversionFactorIsFilledIn = true;
    let biogenicProductUnitKeyIsFilledIn = true;

    const hasValue = (v: any) => v !== null && v !== undefined && v !== '';

    if (optimisticValue?.doesProductContainMoreThan5Percent == YES_NO.YES) {
      biogenicProductConversionFactorIsFilledIn = hasValue(optimisticValue?.productItem?.conversionFactor);
      biogenicProductUnitKeyIsFilledIn = !!optimisticValue?.productItem?.unitKey;
    }

    let biogenicAccompanyingPackageConversionFactorIsFilledIn = true;
    let biogenicAccompanyingPackageUnitKeyIsFilledIn = true;

    if (optimisticValue?.doesPackagingMaterialContainMoreThan5Percent == YES_NO.YES) {
      biogenicAccompanyingPackageConversionFactorIsFilledIn = hasValue(optimisticValue?.accompanyingPackageItem?.conversionFactor);
      biogenicAccompanyingPackageUnitKeyIsFilledIn = !!optimisticValue?.accompanyingPackageItem?.unitKey;
    }

    return (
      !!optimisticValue?.doesProductContainMoreThan5Percent &&
      !!optimisticValue?.doesPackagingMaterialContainMoreThan5Percent &&
      !!biogenicProductConversionFactorIsFilledIn &&
      !!biogenicProductUnitKeyIsFilledIn &&
      !!biogenicAccompanyingPackageConversionFactorIsFilledIn &&
      !!biogenicAccompanyingPackageUnitKeyIsFilledIn
    );
  };

  return (
    <>
      <FieldContainer>
        <FieldPanelInput>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>{t('epdWizard.wizardStep2.properties.biogenicCarbon.title')}</span>
            <TooltipHelpIcon content={t('epdWizard.wizardStep2.properties.biogenicCarbon.tooltip')} position={'right'} />
            {!isSectionValid() && <StatusAttentionIcon />}
          </LabelGroup>
          <HelpBox>{t('epdWizard.wizardStep2.properties.biogenicCarbon.infoBox')}</HelpBox>
        </FieldPanelInput>
        <FieldContainer>
          <WizardSelectControlledComponent
            required={epd?.pcr?.isConstruction}
            label={t('epdWizard.wizardStep2.properties.biogenicCarbon.product5percent')}
            options={YesNoDataSource}
            value={YesNoDataSource.find((item) => item.value === optimisticValue?.doesProductContainMoreThan5Percent)}
            name="doesProductContainMoreThan5Percent"
            onChanged={handleChangeProperty}
          />
        </FieldContainer>
        <FieldContainer>
          <WizardSelectControlledComponent
            required={epd?.pcr?.isConstruction}
            label={t('epdWizard.wizardStep2.properties.biogenicCarbon.package5percent')}
            options={YesNoDataSource}
            value={YesNoDataSource.find(
              (item) => item.value === optimisticValue?.doesPackagingMaterialContainMoreThan5Percent
            )}
            name="doesPackagingMaterialContainMoreThan5Percent"
            onChanged={handleChangeProperty}
          />
        </FieldContainer>
        <FieldContainer>
          <FieldContainer>
            <SmartLabel label={t('epdWizard.wizardStep2.properties.biogenicCarbon.product')} />
          </FieldContainer>
          <FieldPanel>
            <WizardSelectControlledComponent
              required={isProductContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.propertyUnit')}
              options={biogenicCarbonContentUnits ?? []}
              value={biogenicCarbonContentUnits?.find(
                (item) => item.value === optimisticValue?.productItem?.unitKey?.toString()
              )}
              name="productItemUnitKey"
              onChanged={handleChangeProperty}
            />
            <WizardTextInputComponent
              required={isProductContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.value')}
              value={optimisticValue?.productItem?.conversionFactor}
              type="number"
              min="0"
              step="any"
              name="productItemConversionFactor"
              onKeyDown={handleConversionFactorKeyDown}
              onPaste={handleConversionFactorPaste}
              onChange={handleConversionFactorChange}
              onChanged={handleChangeProperty}
            />
          </FieldPanel>
        </FieldContainer>

        <FieldContainer>
          <FieldContainer>
            <SmartLabel label={t('epdWizard.wizardStep2.properties.biogenicCarbon.package')} />
          </FieldContainer>
          <FieldPanel>
            <WizardSelectControlledComponent
              required={isPackageContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.propertyUnit')}
              options={biogenicCarbonContentUnits ?? []}
              value={biogenicCarbonContentUnits?.find(
                (item) => item.value === optimisticValue?.accompanyingPackageItem?.unitKey?.toString()
              )}
              name="accompanyingPackageItemUnitKey"
              onChanged={handleChangeProperty}
            />
            <WizardTextInputComponent
              required={isPackageContentRequired}
              label={t('epdWizard.wizardStep2.properties.biogenicCarbon.value')}
              value={optimisticValue?.accompanyingPackageItem?.conversionFactor}
              type="number"
              min="0"
              step="any"
              name="accompanyingPackageItemConversionFactor"
              onKeyDown={handleConversionFactorKeyDown}
              onPaste={handleConversionFactorPaste}
              onChange={handleConversionFactorChange}
              onChanged={handleChangeProperty}
            />
          </FieldPanel>
        </FieldContainer>
      </FieldContainer>
    </>
  );
};

const FieldPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;
  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

export default BiogenicCarbonContentCopySection;
